import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AddItemsStep from '../components/wizard/AddItemsStep';
import CustomerInfoStep from '../components/wizard/CustomerInfoStep';
import ReviewOrderStep from '../components/wizard/ReviewOrderStep';
import { Order, OrderStatus } from '../types/order';
import { OrderItemCreateInput } from '../types/orderItem';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

export type OrderFormData = {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_contact: string;
  order_date: string;
  required_by_date: string;
  billing_address_id: string;
  delivery_address_id: string;
  special_instructions: string;
  status: OrderStatus;
  billing_address?: {
    address_line1: string;
    address_line2: string;
    city: string;
    region: string;
    postal_code: string;
    country_code: string;
  };
  delivery_address?: {
    address_line1: string;
    address_line2: string;
    city: string;
    region: string;
    postal_code: string;
    country_code: string;
  };
};

const EditOrder = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [orderData, setOrderData] = useState<OrderFormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_contact: '',
    order_date: new Date().toISOString().split('T')[0],
    required_by_date: '',
    billing_address_id: '',
    delivery_address_id: '',
    special_instructions: '',
    status: OrderStatus.PENDING,
  });

  const [orderItems, setOrderItems] = useState<OrderItemCreateInput[]>([]);

  useEffect(() => {
    // Load user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    loadOrderData();
  }, [id]);

  const loadOrderData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [orderRes, itemsRes] = await Promise.all([
        fetch(`/api/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/orders/${id}/items`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const orderResult = await orderRes.json();
      const itemsResult = await itemsRes.json();

      if (orderResult.success) {
        const order: Order = orderResult.data;
        setOrderData({
          customer_name: order.customer_name,
          customer_email: order.customer_email || '',
          customer_phone: order.customer_phone || '',
          customer_contact: order.customer_contact || '',
          order_date: order.order_date ? order.order_date.split('T')[0] : '',
          required_by_date: order.required_by_date ? order.required_by_date.split('T')[0] : '',
          billing_address_id: order.billing_address_id || '',
          delivery_address_id: order.delivery_address_id || '',
          special_instructions: order.special_instructions || '',
          status: order.status,
        });
      }

      if (itemsResult.success && itemsResult.data) {
        const items = itemsResult.data.map((item: any) => {
          const primaryImage = item.asset_template?.images?.find((img: any) => img.variant === 'web');
          const imageUrl = primaryImage?.url || item.asset_template?.images?.[0]?.url;

          return {
            asset_template_id: item.asset_template_id,
            quantity: item.quantity,
            unit_price: parseFloat(item.unit_price),
            rental_length_months: item.rental_length_months || 1,
            _templateName: item.asset_template?.name,
            _imageUrl: imageUrl,
          } as OrderItemCreateInput;
        });
        setOrderItems(items);
      }
    } catch (error) {
      console.error('Error loading order:', error);
      alert('Failed to load order data');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Create addresses first if they're new
      let billingAddressId = orderData.billing_address_id;
      let deliveryAddressId = orderData.delivery_address_id;

      if (orderData.billing_address && orderData.billing_address.address_line1) {
        const billingResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.billing_address),
        });
        const billingResult = await billingResponse.json();
        if (billingResult.success) {
          billingAddressId = billingResult.data.id;
        }
      }

      if (orderData.delivery_address && orderData.delivery_address.address_line1) {
        const deliveryResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.delivery_address),
        });
        const deliveryResult = await deliveryResponse.json();
        if (deliveryResult.success) {
          deliveryAddressId = deliveryResult.data.id;
        }
      }

      const payload = {
        customer_name: orderData.customer_name,
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        customer_contact: orderData.customer_contact,
        order_date: orderData.order_date,
        required_by_date: orderData.required_by_date,
        billing_address_id: billingAddressId,
        delivery_address_id: deliveryAddressId,
        special_instructions: orderData.special_instructions,
        status: orderData.status,
        items: orderItems,
      };

      const response = await fetch(`/api/orders/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update order');
      }

      navigate(`/orders/${id}`);
    } catch (error) {
      console.error('Error updating order:', error);
      alert(error instanceof Error ? error.message : 'Failed to update order');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + item.quantity * item.unit_price * item.rental_length_months, 0);
  };

  const canProceedToStep2 = () => {
    return orderData.customer_name && orderData.customer_email && orderData.customer_phone;
  };

  const canProceedToStep3 = () => {
    return orderItems.length > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading order...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/orders/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order Details
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Edit Order</CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Step 1: Update customer details and delivery information'}
              {currentStep === 2 && 'Step 2: Modify products in this order'}
              {currentStep === 3 && 'Step 3: Review and confirm order changes'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 1 && (
              <CustomerInfoStep data={orderData} onChange={setOrderData} />
            )}

            {currentStep === 2 && <AddItemsStep items={orderItems} onChange={setOrderItems} />}

            {currentStep === 3 && (
              <ReviewOrderStep
                orderData={orderData}
                items={orderItems}
                total={calculateTotal()}
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <div className="flex gap-2">
            {currentStep === 1 ? (
              <Button variant="outline" onClick={() => navigate(`/orders/${id}`)}>
                Cancel
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3)}
                disabled={submitting}
              >
                Back
              </Button>
            )}
          </div>

          <div>
            {currentStep === 1 && (
              <Button onClick={() => setCurrentStep(2)} disabled={!canProceedToStep2()}>
                Next: Add Items
              </Button>
            )}

            {currentStep === 2 && (
              <Button onClick={() => setCurrentStep(3)} disabled={!canProceedToStep3()}>
                Next: Review Order
              </Button>
            )}

            {currentStep === 3 && (
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Updating Order...' : 'Update Order'}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditOrder;

